import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { CalendarCheck, CheckCircle2, Loader2, Phone, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getAvailableSlots, createAppointment } from "@/lib/booking.functions";
import { submitLead } from "@/lib/leads.functions";
import { trackConversion } from "@/lib/openai-ads";
import { trackStandard } from "@/lib/meta-events";
import { getAttribution } from "@/lib/meta-attribution";
import { useLeadGuard } from "@/components/site/lead-honeypot";
import type { Slot } from "@/lib/booking";
import { Button } from "@/components/site/Button";
import { inspectionAddressSchema, formatInspectionAddress } from "@/lib/inspection-address";

type OpenOpts = { leadId?: string };
type Ctx = { openInspection: (opts?: OpenOpts | unknown) => void };

const InspectionDialogContext = createContext<Ctx>({ openInspection: () => {} });

/** Opens the shared free-inspection calendar popup. Safe to call anywhere under the provider. */
export function useInspectionDialog() {
  return useContext(InspectionDialogContext);
}

const PHONE = "480-941-5373";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function InspectionDialogProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [session, setSession] = useState(0);

  const openInspection = useCallback((opts?: OpenOpts | unknown) => {
    const id =
      opts && typeof opts === "object" && "leadId" in opts ? (opts as OpenOpts).leadId : undefined;
    setLeadId(id && UUID_RE.test(id) ? id : null);
    setSession((n) => n + 1);
    setOpen(true);
  }, []);
  const value = useMemo(() => ({ openInspection }), [openInspection]);

  // Email links land on ?book=<leadId> — open the calendar for that customer.
  useEffect(() => {
    const url = new URL(window.location.href);
    const book = url.searchParams.get("book");
    if (book === null) return;
    url.searchParams.delete("book");
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    openInspection(UUID_RE.test(book) ? { leadId: book } : undefined);
  }, [openInspection]);

  return (
    <InspectionDialogContext.Provider value={value}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-[520px]">
          <BookingFlow key={session} initialLeadId={leadId} onClose={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </InspectionDialogContext.Provider>
  );
}

const inputCls =
  "w-full rounded-lg border border-border bg-background px-4 py-3 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30";

export type InspectionBookingProps = {
  initialLeadId?: string | null;
  onClose?: () => void;
  inline?: boolean;
  source?: string;
  serviceTag?: string;
  title?: string;
  onLeadStored?: (id: string) => void;
  onBooked?: (id: string, startAt: string) => void;
  onPhoneClick?: () => void;
};

/** Same calendar and confirmation flow for popups and inline inspection pages. */
export function BookingFlow({ initialLeadId = null, onClose, inline = false, source = "inspection-popup-booking", serviceTag = "", title = "Book your free roof inspection", onLeadStored, onBooked, onPhoneClick }: InspectionBookingProps) {
  const [leadId, setLeadId] = useState<string | null>(initialLeadId);
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [picked, setPicked] = useState<Slot | null>(null);
  const [step, setStep] = useState<"slots" | "details" | "done" | "callback">("slots");
  const [busy, setBusy] = useState(false);
  const [doneLabel, setDoneLabel] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", street: "", city: "", state: "AZ", zip: "", notes: "" });
  const book = useServerFn(createAppointment);
  const submit = useServerFn(submitLead);
  const { honeypot, guardFields } = useLeadGuard();

  function loadSlots() {
    getAvailableSlots()
      .then(setSlots)
      .catch(() => setSlots([]));
  }
  useEffect(loadSlots, []);

  const grouped = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const s of slots ?? []) {
      const list = map.get(s.dayLabel) ?? [];
      list.push(s);
      map.set(s.dayLabel, list);
    }
    return [...map.entries()];
  }, [slots]);

  async function bookWith(id: string, slot: Slot) {
    const res = await book({ data: { leadId: id, startAt: slot.startAt, ...(form.street.trim() ? { address: fullAddress() } : {}) } });
    setDoneLabel((res as { label?: string })?.label ?? `${slot.dayLabel}, ${slot.timeLabel}`);
    setStep("done");
    onBooked?.(id, slot.startAt);
  }

  async function pick(slot: Slot) {
    setPicked(slot);
    if (!leadId) {
      setStep("details");
      return;
    }
    setBusy(true);
    try {
      await bookWith(leadId, slot);
    } catch {
      // Unknown/old link or slot taken — fall back to the short details form.
      setLeadId(null);
      setStep("details");
      loadSlots();
    } finally {
      setBusy(false);
    }
  }

  async function sendLead(message: string, leadType: string) {
    const res = await submit({
      data: {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        message,
        service: serviceTag,
        source,
        attribution: getAttribution({
          funnel: serviceTag ? "hail_damage" : "service_page",
          lead_type: leadType,
          form_name: inline ? "hail_inspection_calendar" : "inspection_popup_calendar",
        }),
        source_page: window.location.pathname,
        property_address: { street: form.street, city: form.city, state: form.state, zip: form.zip },
        ...guardFields(),
      },
    });
    if (res?.stored && res.lead_id) {
      trackConversion("form_submit", { dedupeKey: `form:${res.lead_id}`, leadId: res.lead_id });
      if (res.event_id) {
        // Browser copy of the Meta Lead, deduplicated with the server CAPI event via shared event_id.
        trackStandard(
          "Lead",
          `lead:${res.event_id}`,
          {
            funnel: serviceTag ? "hail_damage" : "service_page",
            lead_type: leadType,
            form_name: inline ? "hail_inspection_calendar" : "inspection_popup_calendar",
          },
          res.event_id,
        );
      }
      onLeadStored?.(res.lead_id);
      return res.lead_id;
    }
    return null;
  }

  function fullAddress() {
    return formatInspectionAddress(inspectionAddressSchema.parse(form));
  }

  function validate(): boolean {
    if (form.name.trim().length < 2) return toast.error("Please enter your name."), false;
    if (form.phone.replace(/\D/g, "").length < 10)
      return toast.error("Please enter a valid 10-digit phone number."), false;
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim()))
      return toast.error("Please enter a valid email."), false;
    const address = inspectionAddressSchema.safeParse(form);
    if (!address.success) return toast.error(address.error.issues[0]?.message ?? "Please enter your property address."), false;
    return true;
  }

  async function onDetails(e: FormEvent) {
    e.preventDefault();
    if (!picked || !validate()) return;
    setBusy(true);
    try {
      const msg = [
        `Booked from calendar popup: ${picked.dayLabel}, ${picked.timeLabel}`,
        `Property address: ${fullAddress()}`,
        form.notes.trim() ? `Notes: ${form.notes.trim()}` : "",
      ]
        .filter(Boolean)
        .join("\n");
      const id = await sendLead(msg, "free_inspection");
      if (!id) throw new Error("no lead");
      setLeadId(id);
      try {
        await bookWith(id, picked);
      } catch {
        toast.error("That time was just taken — please pick another. We still have your info.");
        setPicked(null);
        setStep("slots");
        loadSlots();
      }
    } catch {
      toast.error(`Something went wrong. Please call ${PHONE}.`);
    } finally {
      setBusy(false);
    }
  }

  async function onCallback(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const id = await sendLead(
        ["Requested a call back to schedule an inspection.", `Property address: ${fullAddress()}`, form.notes.trim() && `Notes: ${form.notes.trim()}`]
          .filter(Boolean)
          .join("\n"),
        "free_inspection",
      );
      if (!id) throw new Error("Request was not saved");
      setDoneLabel("");
      setStep("done");
    } catch {
      toast.error(`Something went wrong. Please call ${PHONE}.`);
    } finally {
      setBusy(false);
    }
  }

  const fields = (
    <>
      {honeypot}
      <input className={inputCls} aria-label="Full name" placeholder="Full name" autoComplete="name" required maxLength={80} value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })} />
      <input className={inputCls} aria-label="Phone" placeholder="Phone" type="tel" autoComplete="tel" required maxLength={20} value={form.phone}
        onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      <input className={inputCls} aria-label="Email" placeholder="Email" type="email" autoComplete="email" required maxLength={120} value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })} />
      <input className={inputCls} aria-label="Street address" placeholder="Street address" autoComplete="address-line1" required maxLength={160} value={form.street}
        onChange={(e) => setForm({ ...form, street: e.target.value })} />
      <div className="grid grid-cols-[minmax(0,1fr)_64px_90px] gap-2">
        <input className={inputCls} aria-label="City" placeholder="City" autoComplete="address-level2" required maxLength={80} value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })} />
        <input className={inputCls} aria-label="State" placeholder="State" autoComplete="address-level1" required maxLength={2} value={form.state}
          onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} />
        <input className={inputCls} aria-label="ZIP" placeholder="ZIP" inputMode="numeric" autoComplete="postal-code" required maxLength={10} value={form.zip}
          onChange={(e) => setForm({ ...form, zip: e.target.value })} />
      </div>
      <textarea className={`${inputCls} min-h-[88px] resize-y`} aria-label="Notes (optional)" maxLength={1000}
        placeholder="Notes — what's going on with your roof? (optional)" value={form.notes}
        onChange={(e) => setForm({ ...form, notes: e.target.value })} />
    </>
  );

  const submitCls =
    "inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-lg bg-accent px-6 text-base font-semibold text-primary transition-colors hover:bg-[var(--accent-hover)] disabled:opacity-60";

  return (
    <>
      {inline ? <header className="mb-5">
        <h3 className="text-xl font-semibold text-primary">{step === "done" ? "You're all set" : title}</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {step === "slots" && "Pick a time that works for you."}
          {step === "details" && picked && `${picked.dayLabel}, ${picked.timeLabel} — tell us how to reach you and where the roof is. A sales representative will call beforehand to confirm.`}
          {step === "callback" && "Leave your info and we'll call you to set up a time."}
          {step === "done" && (doneLabel ? "A Durafoam sales representative will call you beforehand to confirm everything. A confirmation email is on its way." : "We've got your request and we'll call you shortly.")}
        </p>
      </header> : <DialogHeader>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-accent">
          Free, no-pressure
        </p>
        <DialogTitle className="text-xl font-semibold text-primary">
            {step === "done" ? "You're all set" : title}
        </DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground">
          {step === "slots" && "Pick a time that works. The visit takes 30–45 minutes and you get a written estimate on site."}
          {step === "details" && picked && `${picked.dayLabel}, ${picked.timeLabel} — tell us how to reach you and where the roof is. A sales representative will call beforehand to confirm.`}
          {step === "callback" && "Leave your info and we'll call you to set up a time."}
          {step === "done" && (doneLabel ? "A Durafoam sales representative will call you beforehand to confirm everything. A confirmation email is on its way." : "We've got your request and we'll call you shortly.")}
        </DialogDescription>
      </DialogHeader>}

      {step === "slots" && (
        <div>
          {slots === null && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading available times…
            </p>
          )}
          {slots !== null && slots.length === 0 && (
            <p className="rounded-xl border border-border bg-card p-4 text-[15px] text-muted-foreground">
              No online times are open right now. Call {PHONE}, or have us call you.
            </p>
          )}
          {grouped.map(([day, daySlots]) => (
            <div key={day} className="mt-4 first:mt-0">
              <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{day}</h3>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {daySlots.map((s) => (
                  <button key={s.startAt} type="button" disabled={busy} onClick={() => void pick(s)}
                    className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl border border-border bg-card px-3 text-sm font-semibold text-foreground transition-colors hover:border-accent hover:bg-accent/10 disabled:opacity-60">
                    {busy && picked?.startAt === s.startAt ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <CalendarCheck className="h-4 w-4 text-accent" aria-hidden="true" />
                    )}
                    {s.timeLabel}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <button type="button" onClick={() => setStep("callback")}
            className="mt-5 inline-flex min-h-[44px] items-center text-sm font-semibold text-primary hover:underline">
            None of these work? Have someone call me
          </button>
        </div>
      )}

      {step === "details" && (
        <form onSubmit={onDetails} className="grid gap-3">
          {fields}
          <button type="submit" disabled={busy} className={submitCls}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <CalendarCheck className="h-4 w-4" aria-hidden="true" />}
            Book this time
          </button>
          <button type="button" onClick={() => setStep("slots")}
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Pick a different time
          </button>
        </form>
      )}

      {step === "callback" && (
        <form onSubmit={onCallback} className="grid gap-3">
          {fields}
          <button type="submit" disabled={busy} className={submitCls}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />} Call me to schedule
          </button>
          <button type="button" onClick={() => setStep("slots")}
            className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to times
          </button>
        </form>
      )}

      {step === "done" && (
        <div className="rounded-xl border border-accent/40 bg-accent/10 p-5">
          <p className="flex items-center gap-2 text-base font-semibold text-foreground">
            <CheckCircle2 className="h-5 w-5 text-accent" aria-hidden="true" />
            {doneLabel || "Request received"}
          </p>
          {onClose && <Button type="button" onClick={onClose} className={`${submitCls} mt-4`}>Done</Button>}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-accent" aria-hidden="true" /> Licensed ROC223900
        </span>
        <a href="tel:+14809415373" onClick={onPhoneClick} className="flex items-center gap-2 text-sm font-semibold text-primary">
          <Phone className="h-4 w-4" aria-hidden="true" /> Or call {PHONE}
        </a>
      </div>
    </>
  );
}
